// Phase B — Customer Portal · My Requests
//
// Read-only list of the customer's own deals. The server applies the
// view_tier filter (customer_tenant_id = user.tenant_id) and strips
// internal cost/margin fields before they ever hit the wire. The FE
// makes the same assumption defensively — it only reads the fields it
// expects to see.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ListChecks, ChevronRight, Inbox, Sparkles, FilePlus } from "lucide-react";
import { useLogisticsRequests } from "../../api/logistics";
import { useT } from "../../lib/i18n";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const STAGE_KEYS = [
  "registered", "assigned", "waiting_for_pricing", "priced", "waiting_for_approval",
  "to_send", "offered", "won", "executed", "lost", "cancelled",
];

const STAGE_COLOR = {
  registered: "#79b5f0",
  offered: "#c89adb",
  won: "#4ed084",
  executed: "#4ed084",
  cancelled: "#9aa1b3",
  lost: "#ff8a8a",
};

export default function PortalMyRequests() {
  const { t } = useT();
  const navigate = useNavigate();
  const { data, isLoading } = useLogisticsRequests();
  const items = data?.items || [];

  return (
    <div data-testid="portal-my-requests-page">
      <header className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 28px)", color: "var(--text, #f0f2f8)" }}>
            <ListChecks size={22} style={{ display: "inline", marginRight: 8, marginBottom: 4 }} />
            {t("portal.list.title")}
          </h1>
          <div className="mt-1" style={mono(11, "var(--text-dim, #c5cad8)")}>
            {t("portal.list.subtitle")}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/portal/new" className="px-3 py-2 rounded inline-flex items-center gap-2"
            style={{ background: "rgba(155,89,182,0.18)", color: "#e0bff0", border: "1px solid rgba(155,89,182,0.45)", ...mono(10, "#e0bff0", 700, "0.8px"), textTransform: "uppercase", textDecoration: "none" }}
            data-testid="portal-my-requests-new-link"
          >
            <FilePlus size={12} /> {t("portal.nav.new_request")}
          </Link>
          <Link to="/portal/chat" className="px-3 py-2 rounded inline-flex items-center gap-2"
            style={{ background: "rgba(155,89,182,0.06)", color: "#c89adb", border: "1px solid rgba(155,89,182,0.30)", ...mono(10, "#c89adb", 700, "0.8px"), textTransform: "uppercase", textDecoration: "none" }}
            data-testid="portal-my-requests-chat-link"
          >
            <Sparkles size={12} /> {t("portal.nav.chat")}
          </Link>
        </div>
      </header>

      <div
        className="rounded overflow-x-auto"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
        data-testid="portal-my-requests-table"
      >
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.03)" }}>
              {[
                t("portal.list.col_request"),
                t("portal.list.col_route"),
                t("portal.list.col_cargo"),
                t("portal.list.col_stage"),
                t("portal.list.col_submitted"),
                "",
              ].map((h, idx) => (
                <th key={idx} style={{ padding: "8px 12px", textAlign: "left", ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", ...mono(11, "var(--text-dim, #9aa1b3)") }}>{t("portal.list.loading")}</td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 28, textAlign: "center" }} data-testid="portal-my-requests-empty">
                  <Inbox size={28} style={{ display: "inline-block", color: "#6b7388", marginBottom: 8 }} />
                  <div style={mono(12, "var(--text-dim, #c5cad8)")}>
                    {t("portal.list.empty")}
                  </div>
                </td>
              </tr>
            )}
            {items.map((r) => {
              const pickup = (r.pickups || [])[0] || {};
              const dropoff = (r.dropoffs || [])[0] || {};
              const stageLabel = STAGE_KEYS.includes(r.deal_stage) ? t(`portal.stage.${r.deal_stage}`) : (r.deal_stage || "");
              const stageColor = STAGE_COLOR[r.deal_stage] || "#cfd5e3";
              return (
                <tr
                  key={r.id}
                  data-testid={`portal-request-row-${r.id}`}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: "pointer" }}
                  onClick={() => navigate(`/portal/r/${r.id}`)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(155,89,182,0.05)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ ...mono(12, "var(--text, #f0f2f8)", 600) }}>{r.deal_name || r.id}</div>
                    <div style={mono(10, "var(--text-dim, #6b7388)")}>{r.id}</div>
                  </td>
                  <td style={{ padding: "10px 12px", ...mono(11, "var(--text-dim, #cfd5e3)") }}>
                    {[pickup.city, dropoff.city].filter(Boolean).join(" → ") || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", ...mono(11, "var(--text-dim, #cfd5e3)") }}>
                    {r.aru_megnevezese || "—"}
                    {r.suly_kg ? ` · ${r.suly_kg} kg` : ""}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
                      style={{
                        background: `${stageColor}22`,
                        color: stageColor,
                        border: `1px solid ${stageColor}55`,
                        ...mono(10, stageColor, 600, "0.6px"),
                        textTransform: "uppercase",
                      }}
                      data-testid={`portal-stage-${r.id}`}
                    >
                      {stageLabel}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", ...mono(10, "var(--text-dim, #9aa1b3)") }}>
                    {(r.created_at || "").slice(0, 10)}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <ChevronRight size={14} color="#6b7388" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
